import { Auth0Client } from '@auth0/auth0-spa-js';
import env from './env';

/**
 * VITE_ENVIRON is exported so other modules can do environment checks.
 * Named VITE_ENVIRON (was REACT_APP_ENVIRON) — update any imports from this file.
 */
export const VITE_ENVIRON = env.environ;

// ─── Auth0 ────────────────────────────────────────────────────────────────────

// MissionPortal-DB audience has offline_access enabled in Auth0.
// This allows us to use refresh tokens which are automatically rotated by Auth0.
// Refresh token rotation reduces security risks from compromised tokens and
// mitigates negative effects of browser privacy features.
export const authconfig = {
  domain: env.auth0Domain,
  clientId: env.auth0ClientId,
  audience: 'MissionPortal-DB',
};

// ─── AWS Amplify v6 ───────────────────────────────────────────────────────────

// API.GraphQL.defaultAuthMode: 'openidConnect' — uses Auth0 tokens for AppSync.
// Auth.Cognito.identityPoolId: legacy config kept for Amplify backward compat.
export const awsconfig = {
  API: {
    GraphQL: {
      endpoint: env.graphqlEndpoint,
      region: 'us-east-1',
      defaultAuthMode: 'openidConnect',
    },
  },
  Auth: {
    Cognito: {
      identityPoolId: env.cognitoIdPool,
      region: 'us-east-1',
    },
  },
};

// ─── Auth0 client (used for token acquisition outside of React context) ───────
// Singleton: constructed once at module load so every getAuthToken() call
// reuses the same instance and its internal token cache.

const auth0Client = new Auth0Client({
  domain: env.auth0Domain,
  clientId: env.auth0ClientId,
  authorizationParams: {
    redirect_uri: `${window.location.origin}/callback`,
    audience: 'MissionPortal-DB',
    scope: 'openid email profile read:current_user update:current_user_metadata offline_access',
  },
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
});

/**
 * Get Auth0 access token for API calls.
 * Any acquisition failure (expired token, network error) returns null —
 * callers treat null as unauthenticated and trigger logout.
 * @returns {Promise<string|null>} The auth token or null if not authenticated.
 */
export const getAuthToken = async () => {
  try {
    return await auth0Client.getTokenSilently({ cacheMode: 'on' });
  } catch {
    return null;
  }
};

/**
 * Get authorization headers for WebSocket/API requests.
 * @returns {Promise<Object>} Headers object with Authorization token.
 */
export const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return token ? { Authorization: token } : {};
};

// ─── Third-party licenses ─────────────────────────────────────────────────────

const dromoFrontendLicense = {
  production: '9cce1b92-9df8-4b6d-904b-40f231513101',
  staging: '171ccc59-1c10-434d-9a39-e4ea3bba0445',
};

export const dromoFrontendLic = env.environ === 'production'
  ? dromoFrontendLicense.production
  : dromoFrontendLicense.staging;

// expires on Jun 5, 2027
export const MuiProLic = '4f7a59064a561c4a3d8ee117be70c195Tz0xMjk5MzYsRT0xODEyMzI2Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MyxBVD1zaW5nbGUsS1Y9Mg==';
